import React, { useState } from "react";
import api from "../api/client";
import * as XLSX from "xlsx";

/* -------------------------------
   DB FIELD CONFIG
-------------------------------- */
const DB_FIELDS = [
  { key: "product_code", label: "Product Code", defaultType: "string", required: true },
  { key: "name", label: "Name", defaultType: "string" },
  { key: "alias_name", label: "Alias Name", defaultType: "string" },
  { key: "description", label: "Description", defaultType: "string" },
  { key: "price", label: "Price", defaultType: "decimal" },
  { key: "cost_price", label: "Cost Price", defaultType: "decimal" },
  { key: "mrp", label: "MRP", defaultType: "decimal" },
  { key: "status", label: "Status", defaultType: "enum" },
  { key: "category_id", label: "Category ID", defaultType: "bigint" },
  { key: "product_type", label: "Product Type", defaultType: "string" },
  { key: "channel", label: "Channel", defaultType: "string" },
];

const TYPE_OPTIONS = ["string", "decimal", "bigint", "enum", "datetime"];

/* -------------------------------
   MAIN COMPONENT
-------------------------------- */
const ProductBulkImport = () => {
  const [file, setFile] = useState(null);
  const [columns, setColumns] = useState([]);          // [{ column: 'A', header: 'Price' }]
  const [mapping, setMapping] = useState({});          // { A: 'price' }
  const [typeHints, setTypeHints] = useState({});      // { price: 'decimal' }

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [error, setError] = useState("");
  const [dryRun, setDryRun] = useState(false);

  /* -------------------------------
     READ EXCEL HEADERS
  -------------------------------- */
  const readExcelColumns = async (file) => {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf);
    const sheet = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
    const headers = rows[0] || [];

    return headers.slice(0, 26).map((h, i) => ({
      column: String.fromCharCode(65 + i), // A → Z
      header: String(h || "")
    }));
  };

  /* -------------------------------
     CONVERT COLUMN MAPPING TO HEADER MAPPING
  -------------------------------- */
  const columnMappingToHeaderMapping = (mapping, columns) => {
    const headerMapping = {};

    for (const [colLetter, dbField] of Object.entries(mapping)) {
      const col = columns.find(c => c.column === colLetter);

      if (!col?.header) {
        throw new Error(`Column ${colLetter} has no header`);
      }

      headerMapping[col.header] = dbField;
    }

    return headerMapping;
  };

  /* -------------------------------
     FILE CHANGE
  -------------------------------- */
  const handleFileChange = async (e) => {
    const f = e.target.files[0];
    if (!f) return;

    setFile(f);
    setPreview(null);
    setBatchId(null);
    setError("");

    const cols = await readExcelColumns(f);
    
    setColumns(cols);

    // reset mapping
    setMapping({});
    setTypeHints({});
  };

  /* -------------------------------
     PREVIEW
  -------------------------------- */
  const handlePreview = async () => {
    if (!file) return alert("Select an Excel file first");

    if (!Object.values(mapping).includes("product_code")) {
      return alert("Product Code must be mapped");
    }

    setLoading(true);
    setError("");
    setPreview(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("indexBy", "product_code");
      
      const headerMapping = columnMappingToHeaderMapping(mapping, columns);
      formData.append("mapping", JSON.stringify(headerMapping));
      formData.append("typeHints", JSON.stringify(typeHints));

      const res = await api.post(
        `/products/admin/import/preview`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" }
        }
      );

      console.log("Preview response:", res.data);
      setPreview(res.data.data);
    } catch (err) {
      console.error("Preview error:", err);
      setError(err.response?.data?.message || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------
     EXECUTE
  -------------------------------- */
  const handleExecute = async () => {
    if (!preview?.preview?.length) {
      return alert("No valid rows to import");
    }

    setLoading(true);
    setError("");

    try {
      const res = await api.post(
        `/products/admin/import`,
        {
          indexBy: "product_code",
          fileName: file.name,
          dryRun,
          rows: preview.preview.map(r => {
            const d = { ...r.data };
            delete d.product_code; // 🔒 NEVER update index
            return {
              indexValue: r.indexValue,
              data: d
            };
          })
        },
        { headers: { "Content-Type": "application/json" } }
      );

      console.log("Execute response:", res.data);
      
      // Extract batchId from response
      const responseBatchId = res.data.data?.batchId || res.data.batchId;
      setBatchId(responseBatchId);
      
      alert("Import executed successfully");
    } catch (err) {
      console.error("Execute error:", err);
      setError(err.response?.data?.message || "Execution failed");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------
     REVERT
  -------------------------------- */
  const handleRevert = async () => {
    if (!batchId) return;

    if (!window.confirm("Revert this import?")) return;

    setLoading(true);
    setError("");

    try {
      await api.post(
        `/products/admin/import/${batchId}/revert`,
        {},
        { headers: { "Content-Type": "application/json" } }
      );

      alert("Import reverted successfully");
    } catch (err) {
      console.error("Revert error:", err);
      setError(err.response?.data?.message || "Revert failed");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------
     UI
  -------------------------------- */
  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">
        Product Bulk Import (Admin)
      </h1>

      {/* Upload */}
      <div className="bg-white border rounded-xl p-6 mb-6 shadow-sm">
        <label className="block font-medium mb-2">Excel File (.xlsx)</label>
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          className="block w-full border rounded px-3 py-2"
        />
      </div>

      {/* Column Mapper */}
      {columns.length > 0 && (
        <div className="bg-white border rounded-xl p-6 mb-6 shadow-sm">
          <h2 className="font-semibold text-lg mb-4">Column Mapping</h2>

          <table className="w-full text-sm border">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-3 py-2">Excel Column</th>
                <th className="border px-3 py-2">Header</th>
                <th className="border px-3 py-2">Map to DB Field</th>
                <th className="border px-3 py-2">Type</th>
              </tr>
            </thead>
            <tbody>
              {columns.map(col => {
                const mappedField = mapping[col.column];
                const fieldMeta = DB_FIELDS.find(f => f.key === mappedField);

                return (
                  <tr key={col.column}>
                    <td className="border px-3 py-2">
                      <input
                        className="border rounded px-2 py-1 w-16 font-mono uppercase"
                        value={col.column}
                        maxLength={1}
                        onChange={(e) => {
                          const newCol = e.target.value.toUpperCase().replace(/[^A-Z]/g, "");
                          if (!newCol) return;
                          
                          if (columns.some(c => c.column === newCol && c !== col)) {
                            alert(`Column ${newCol} already exists`);
                            return;
                          }

                          setColumns(prev =>
                            prev.map(c =>
                              c.column === col.column ? { ...c, column: newCol } : c
                            )
                          );

                          setMapping(prev => {
                            const next = { ...prev };
                            if (next[col.column]) {
                              next[newCol] = next[col.column]; // move mapping
                              delete next[col.column];
                            }
                            return next;
                          });
                        }}
                      />
                    </td>

                    <td className="border px-3 py-2">{col.header || "(empty)"}</td>

                    <td className="border px-3 py-2">
                      <select
                        className="border rounded px-2 py-1 w-full"
                        value={mappedField || ""}
                        onChange={e => {
                          const val = e.target.value || null;
                          const next = { ...mapping };

                          // enforce unique product_code
                          if (val === "product_code") {
                            Object.keys(next).forEach(k => {
                              if (next[k] === "product_code") delete next[k];
                            });
                          }

                          if (val) next[col.column] = val;
                          else delete next[col.column];

                          setMapping(next);

                          if (val && !typeHints[val]) {
                            setTypeHints({
                              ...typeHints,
                              [val]: fieldMeta?.defaultType || "string"
                            });
                          }
                        }}
                      >
                        <option value="">Ignore</option>
                        {DB_FIELDS.map(f => (
                          <option key={f.key} value={f.key}>
                            {f.label}{f.required ? " *" : ""}
                          </option>
                        ))}
                      </select>
                    </td>

                    <td className="border px-3 py-2">
                      {mappedField ? (
                        <select
                          className="border rounded px-2 py-1 w-full"
                          value={typeHints[mappedField] || "string"}
                          onChange={e =>
                            setTypeHints({
                              ...typeHints,
                              [mappedField]: e.target.value
                            })
                          }
                        >
                          {TYPE_OPTIONS.map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <button
            onClick={handlePreview}
            disabled={loading}
            className="mt-5 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
          >
            {loading ? "Processing..." : "Preview Import"}
          </button>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="bg-white border rounded-xl p-6 mb-6 shadow-sm">
          <h2 className="font-semibold text-lg mb-3">Preview Result</h2>

          <div className="grid grid-cols-3 gap-4 text-sm mb-4">
            <div className="p-3 bg-gray-50 rounded">
              <span className="text-gray-600">Total Rows:</span> 
              <span className="font-semibold ml-2">{preview.totalRows}</span>
            </div>
            <div className="p-3 bg-green-50 rounded">
              <span className="text-green-600">Valid Rows:</span> 
              <span className="font-semibold ml-2">{preview.validRows}</span>
            </div>
            <div className="p-3 bg-red-50 rounded">
              <span className="text-red-600">Invalid Rows:</span> 
              <span className="font-semibold ml-2">{preview.invalidRows}</span>
            </div>
          </div>

          {/* Show unknown products */}
          {preview.unknownProducts?.length > 0 && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
              <h3 className="font-medium text-yellow-800 mb-2">Unknown Products:</h3>
              <p className="text-sm text-yellow-700">
                {preview.unknownProducts.join(", ")}
              </p>
            </div>
          )}

          {/* Show errors */}
          {preview.errors?.length > 0 && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded max-h-60 overflow-y-auto">
              <h3 className="font-medium text-red-800 mb-2">Errors:</h3>
              {preview.errors.slice(0, 10).map((err, idx) => (
                <div key={idx} className="text-sm text-red-700 mb-1">
                  Row {err.row}: {err.error} {err.field && `(${err.field})`}
                </div>
              ))}
              {preview.errors.length > 10 && (
                <p className="text-sm text-red-600 mt-2">
                  ... and {preview.errors.length - 10} more errors
                </p>
              )}
            </div>
          )}

          {/* Show preview sample */}
          {preview.preview?.length > 0 && (
            <div className="mb-4">
              <h3 className="font-medium mb-2">Sample Valid Rows:</h3>
              <div className="overflow-x-auto max-h-60 overflow-y-auto">
                <table className="min-w-full text-xs border">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="border px-2 py-1">Row</th>
                      <th className="border px-2 py-1">Product Code</th>
                      <th className="border px-2 py-1">Fields to Update</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.slice(0, 5).map((row, idx) => (
                      <tr key={idx}>
                        <td className="border px-2 py-1">{row.rowNumber}</td>
                        <td className="border px-2 py-1 font-mono">{row.indexValue}</td>
                        <td className="border px-2 py-1">
                          {Object.keys(row.data).filter(k => k !== 'product_code').join(", ")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <button
            onClick={handleExecute}
            disabled={loading || preview.validRows === 0}
            className="mt-4 bg-green-600 text-white px-5 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400"
          >
            {loading ? "Executing..." : dryRun ? "Run Dry Import" : "Execute Import"}
          </button>
          <label className="ml-4 inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
            Dry run only
          </label>
        </div>
      )}

      {/* Revert */}
      {batchId && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 shadow-sm">
          <p className="text-sm text-red-700 mb-3">
            Batch ID: <span className="font-mono font-semibold">{batchId}</span>
          </p>
          <button
            onClick={handleRevert}
            disabled={loading}
            className="bg-red-600 text-white px-5 py-2 rounded-lg hover:bg-red-700 disabled:bg-gray-400"
          >
            {loading ? "Reverting..." : "Revert Import"}
          </button>
        </div>
      )}

      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 font-medium">{error}</p>
        </div>
      )}
    </div>
  );
};

export default ProductBulkImport;
